import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import API from "../api";
import "../styles/layout.css";

const MENU_LINKS = [
  { href: "/about", label: "About Us", icon: "fa-solid fa-building" },
  { href: "/#services", label: "Services", icon: "fa-solid fa-hand-holding-medical" },
  { href: "/#portals", label: "Portals", icon: "fa-solid fa-door-open" },
  { href: "/#search", label: "Find a Doctor", icon: "fa-solid fa-user-doctor" },
  { href: "/contact", label: "Contact", icon: "fa-solid fa-envelope" },
];

export default function HomeNavbar({ user, setUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 960px)");
    const onChange = (e) => e.matches && setMenuOpen(false);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  /* Close on outside click */
  useEffect(() => {
    const handleOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && !e.target.closest('.mobile-hamburger')) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [menuOpen]);

  /* Lock body scroll when menu is open */
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  async function handleLogout() {
    try { await API.post("/auth/logout"); } catch (e) {}
    localStorage.removeItem("user");
    setUser(null);
    setMenuOpen(false);
    navigate("/");
  }

  /* ── Desktop auth buttons ── */
  const desktopAuth = !user ? (
    <>
      <Link to="/login" style={{ padding: '8px 18px', borderRadius: '12px', background: '#7c3aed', color: '#fff', textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem', boxShadow: '0 4px 14px rgba(124,58,237,0.3)', transition: 'all 0.2s' }} onClick={() => setMenuOpen(false)}>
        Sign in
      </Link>
      <Link to="/signup" style={{ padding: '8px 18px', borderRadius: '12px', background: '#7c3aed', color: '#fff', textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem', boxShadow: '0 4px 14px rgba(124,58,237,0.3)', transition: 'all 0.2s' }} onClick={() => setMenuOpen(false)}>
        Register now
      </Link>
    </>
  ) : (
    <>
      <Link to="/dashboard" style={{ padding: '8px 18px', borderRadius: '12px', background: '#7c3aed', color: '#fff', textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem', boxShadow: '0 4px 14px rgba(124,58,237,0.3)' }} onClick={() => setMenuOpen(false)}>
        Dashboard
      </Link>
      <button type="button" onClick={handleLogout} style={{ padding: '8px 18px', borderRadius: '12px', background: 'transparent', color: '#dc2626', border: '1px solid #fca5a5', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>
        Logout
      </button>
    </>
  );

  /* ── Mobile dropdown rendered via Portal (at <body> level) ── 
     NO className to avoid styles.css max-width:480px constraint */
  const mobileDropdown = menuOpen ? ReactDOM.createPortal(
    <AnimatePresence>
      {menuOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="homenav-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setMenuOpen(false)}
            style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0, 0, 0, 0.4)',
              zIndex: 9998
            }}
          />
          {/* Full-screen dropdown panel */}
          <motion.div
            key="homenav-dropdown"
            ref={menuRef}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              maxWidth: '100vw',
              background: '#ffffff',
              borderRadius: '0 0 28px 28px',
              padding: '0',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 9999,
              boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
              boxSizing: 'border-box',
              overflow: 'hidden'
            }}
          >
            {/* Top bar: Logo + Close */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '18px 24px',
              borderBottom: '1px solid rgba(124,58,237,0.08)'
            }}>
              <Link to="/" onClick={() => setMenuOpen(false)} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', gap: 8 }}>
                <i className="fa-solid fa-notes-medical" style={{ fontSize: '1.4rem', color: '#7c3aed' }} />
                <span style={{ color: '#4c1d95', fontSize: '1.4rem', fontWeight: 800, fontFamily: 'var(--font-display)' }}>
                  MediSure <span style={{ color: '#7c3aed', fontSize: '0.9rem', fontWeight: 700, verticalAlign: 'top' }}>HEALTH</span>
                </span>
              </Link>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
                style={{
                  width: 40, height: 40,
                  borderRadius: 12,
                  background: '#7c3aed',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(124,58,237,0.3)'
                }}
              >
                <i className="fa-solid fa-xmark" style={{ color: '#fff', fontSize: '1.05rem' }} />
              </button>
            </div>

            {/* Nav links */}
            <nav style={{ padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {MENU_LINKS.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={() => setMenuOpen(false)}
                  style={{
                    padding: '14px 20px',
                    borderRadius: 14,
                    color: '#4c1d95',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    transition: 'background 0.15s ease',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(124,58,237,0.06)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <i className={l.icon} style={{ width: 24, fontSize: '0.95rem', color: '#7c3aed', opacity: 0.7 }} />
                  {l.label}
                </a>
              ))}
            </nav>

            {/* Divider */}
            <div style={{
              height: 1,
              background: 'linear-gradient(90deg, transparent 5%, rgba(124,58,237,0.12) 50%, transparent 95%)',
              margin: '4px 24px'
            }} />

            {/* Auth actions */}
            <div style={{
              display: 'flex',
              gap: '12px',
              padding: '16px 24px 24px',
              alignItems: 'center'
            }}>
              {!user ? (
                <>
                  <Link to="/login" onClick={() => setMenuOpen(false)} style={{
                    flex: 1, textAlign: 'center',
                    padding: '14px 16px', borderRadius: 14,
                    background: 'linear-gradient(135deg, #7c3aed, #6d28d9)', color: '#fff',
                    textDecoration: 'none', fontWeight: 600, fontSize: '0.95rem',
                    boxShadow: '0 4px 16px rgba(124,58,237,0.3)',
                    transition: 'all 0.2s'
                  }}>
                    Sign in
                  </Link>
                  <Link to="/signup" onClick={() => setMenuOpen(false)} style={{
                    flex: 1, textAlign: 'center',
                    padding: '14px 16px', borderRadius: 14,
                    background: 'linear-gradient(135deg, #7c3aed, #6d28d9)', color: '#fff',
                    textDecoration: 'none', fontWeight: 600, fontSize: '0.95rem',
                    boxShadow: '0 4px 16px rgba(124,58,237,0.3)',
                    transition: 'all 0.2s'
                  }}>
                    Register now
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/dashboard" onClick={() => setMenuOpen(false)} style={{
                    flex: 1, textAlign: 'center',
                    padding: '14px 16px', borderRadius: 14,
                    background: 'linear-gradient(135deg, #7c3aed, #6d28d9)', color: '#fff',
                    textDecoration: 'none', fontWeight: 600, fontSize: '0.95rem',
                    boxShadow: '0 4px 16px rgba(124,58,237,0.3)'
                  }}>
                    Dashboard
                  </Link>
                  <button type="button" onClick={handleLogout} style={{
                    flex: 1, textAlign: 'center',
                    padding: '14px 16px', borderRadius: 14,
                    background: 'rgba(220,38,38,0.06)', color: '#dc2626',
                    border: '1px solid rgba(220,38,38,0.15)',
                    fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer'
                  }}>
                    Logout
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  ) : null;

  return (
    <>
      <div className={`topbar-wrapper home-topbar ${scrolled || !isHome ? 'is-scrolled' : ''}`} style={{ position: 'fixed', top: (scrolled || !isHome) ? 10 : 18, transition: 'top 0.3s' }}>
        
        {/* LEFT: LOGO */}
        <div className="topbar-logo-side">
          <Link to="/" className="logo" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', whiteSpace: 'nowrap' }}>
            <i className="fa-solid fa-notes-medical" style={{ marginRight: '8px', fontSize: '1.6rem', color: '#ffffff' }}></i>
            <span style={{ color: '#ffffff', fontSize: '1.6rem', fontWeight: 800, letterSpacing: '-0.02em', fontFamily: 'var(--font-display)', whiteSpace: 'nowrap' }}>
              MediSure <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '1.1rem', fontWeight: 700, verticalAlign: 'top' }}>HEALTH</span>
            </span>
          </Link>
        </div>

        {/* THE PILL: NAV ONLY */}
        <header className="topbar-pill" style={{ background: scrolled ? 'rgba(255, 255, 255, 0.9)' : '#ffffff', backdropFilter: scrolled ? 'blur(10px)' : 'none' }}>
          <nav className="topbar-nav-desktop">
            <ul className="topbar-nav-links">
              {MENU_LINKS.map((l) => (
                <li key={l.label}>
                  <a href={l.href}>{l.label}</a>
                </li>
              ))}
            </ul>
          </nav>
        </header>

        {/* RIGHT: UTILS */}
        <div className="topbar-right">
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }} className="desktop-actions">
             {desktopAuth}
          </div>

          {/* Hamburger (mobile only) */}
          <button
            type="button"
            className="icon-btn mobile-hamburger mobile-only"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            style={{
              background: '#ffffff',
              border: '1px solid rgba(0,0,0,0.1)',
              width: 42, height: 42, borderRadius: 12,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              transition: 'all 0.25s ease'
            }}
          >
            <i className="fa-solid fa-bars" style={{ color: '#4c1d95', fontSize: '1rem' }} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Portal-rendered dropdown (at <body> level, no CSS class interference) */}
      {mobileDropdown}
    </>
  );
}
